import { DestroyRef, Signal, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { combineLatest } from 'rxjs'

import {
  type ConversationSearchSession,
  type ConversationsSearchState,
  activeChatSessionIdStore,
  clearConversationsSearch,
  conversationsSearchStore,
  mapConversationsToSearchSessions,
  requestConversationsSearch,
} from '@flowx/core-sdk'

export type { ConversationSearchSession }

export interface ConversationsSearch {
  searchText: Signal<string>
  isSearchActive: Signal<boolean>
  appliedQuery: Signal<string>
  searchSessions: Signal<ConversationSearchSession[]>
  searchLoading: Signal<boolean>
  onSearchChange: (value: string) => void
  clearSearch: () => void
}

export function createConversationsSearch(
  getBusinessCaseId: () => string | undefined,
  destroyRef: DestroyRef
): ConversationsSearch {
  const searchText = signal('')
  const searchState = signal<{
    query: string
    sessions: ConversationSearchSession[]
    loading: boolean
  }>({ query: '', sessions: [], loading: false })

  combineLatest([conversationsSearchStore.stream$, activeChatSessionIdStore.stream$])
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(([search, currentId]: [ConversationsSearchState, string | null]) => {
      searchState.set({
        query: search.query,
        sessions: mapConversationsToSearchSessions(search.results, currentId),
        loading: search.loading,
      })
    })

  // The consuming chat component unmounts on navigation — drop any stale results.
  destroyRef.onDestroy(() => clearConversationsSearch())

  const onSearchChange = (value: string): void => {
    searchText.set(value ?? '')
    requestConversationsSearch(getBusinessCaseId(), value ?? '')
  }

  const clearSearch = (): void => {
    searchText.set('')
    clearConversationsSearch()
  }

  return {
    searchText: searchText.asReadonly(),
    isSearchActive: computed(() => searchState().query.length > 0),
    appliedQuery: computed(() => searchState().query),
    searchSessions: computed(() => searchState().sessions),
    searchLoading: computed(() => searchState().loading),
    onSearchChange,
    clearSearch,
  }
}
